import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Info, KeyRound, MailCheck } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useSearchParams } from "react-router";
import { Field } from "../admin/ui.tsx";
import { signIn, useSession, useSessionActions } from "../admin/session.ts";
import { api } from "../api/client.ts";
import { isApiRequestError } from "../api/errors.ts";
import {
  magicLinkTargets,
  passkeysSupported,
  requestMagicLink,
  signInWithPasskey,
} from "../auth/diver-auth.ts";
import type { AuthFailure } from "../auth/diver-auth.ts";
import { safeNext } from "../auth/next.ts";
import { Button } from "../components/Button.tsx";
import { useLocale } from "../i18n/index.ts";

const PASSWORD_OPEN_KEY = "divespots.loginPassword";

const readOpen = (): boolean => {
  try {
    return sessionStorage.getItem(PASSWORD_OPEN_KEY) === "1";
  } catch {
    return false;
  }
};
const writeOpen = (open: boolean) => {
  try {
    sessionStorage.setItem(PASSWORD_OPEN_KEY, open ? "1" : "0");
  } catch {
    /* not remembered */
  }
};

/** What the sign-in link or passkey said went wrong, in words the visitor can act on. */
function failureText(t: (k: string) => string, f: AuthFailure): string {
  return f === "banned"
    ? t("diver.login.banned")
    : f === "rate_limited"
      ? t("diver.login.rateLimited")
      : f === "network"
        ? t("diver.login.network")
        : t("diver.login.failed");
}

/**
 * The one login for everybody. Divers use the e-mail link (the same form creates the account on a
 * first visit: nothing exists until the link is used) or a passkey; staff and anyone with a password
 * open "Mit Passwort anmelden". The page never asks who the visitor is: the same fields for all, and
 * a failed password login says one generic thing whoever the account (or no account) is.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const session = useSession();
  const actions = useSessionActions();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const passwordId = useId();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordOpen, setPasswordOpen] = useState(readOpen);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Sign in comes first; registering is the secondary choice, kept in the URL (?mode=register) so it can
  // be linked to and the browser's Back button returns to the sign-in.
  const mode = params.get("mode") === "register" ? "register" : "signin";
  const heading = useRef<HTMLHeadingElement>(null);
  const shownMode = useRef(mode);
  useEffect(() => {
    // after the view changes (a switch button or Back), keyboard and screen reader land on the heading
    if (shownMode.current !== mode) heading.current?.focus();
    shownMode.current = mode;
  }, [mode]);

  // GET /api/config: false when registration is closed (or there is no mail to send links with)
  const config = useQuery({
    queryKey: ["config"],
    queryFn: ({ signal }) => api.publicConfig(signal),
    staleTime: 60_000,
    retry: false,
  });
  const registrationClosed = config.data?.registrationOpen === false;
  const registering = mode === "register" && !registrationClosed;
  const switchTo = (m: "signin" | "register") => {
    setError(null);
    const q = new URLSearchParams(params); // next= and anything else stays
    if (m === "register") q.set("mode", "register");
    else q.delete("mode");
    setParams(q); // a history entry: Back returns to the other view
  };

  // Where to go afterwards: ?next= when it is a path on this site, else the map. (Older links
  // carried the page in the router state.)
  const stateFrom = (location.state as { from?: string } | null)?.from;
  const next = safeNext(params.get("next")) ?? safeNext(stateFrom);
  const after = next ?? `/${locale}/`;
  if (session.status === "user") return <Navigate to={after} replace />;

  // better-auth sends a failed link back here with ?error=CODE
  const linkError = params.get("error");
  const linkErrorText = linkError
    ? failureText(t, /banned/i.test(linkError) ? "banned" : "failed")
    : null;

  const send = async () => {
    setError(null);
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return setError(t("diver.login.badEmail"));
    setBusy(true);
    const res = await requestMagicLink(
      email.trim(),
      registering ? name.trim() || undefined : undefined,
      magicLinkTargets(window.location.origin, locale, next),
    );
    setBusy(false);
    if (res.ok) return setSentTo(email.trim());
    setError(failureText(t, res.reason));
  };

  const withPassword = async () => {
    setError(null);
    if (!email.trim() || !password) return setError(t("diver.login.passwordMissing"));
    setBusy(true);
    try {
      await signIn(email.trim(), password);
      await actions.afterSignIn(); // the session appears and this page redirects
    } catch (e) {
      // ONE answer for every failure of the credentials (wrong password, no such account, an account
      // without a password, a blocked one): telling them apart would say who has an account here
      const code = isApiRequestError(e) ? e.code : "internal";
      setError(
        code === "rate_limited"
          ? t("diver.login.rateLimited")
          : code === "network"
            ? t("diver.login.network")
            : t("diver.login.wrongCredentials"),
      );
    } finally {
      setBusy(false);
    }
  };

  const passkey = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await signInWithPasskey();
      if (res.ok) await actions.afterSignIn();
      else setError(failureText(t, res.reason ?? "failed"));
    } catch {
      setError(failureText(t, "failed"));
    } finally {
      setBusy(false);
    }
  };

  if (sentTo) {
    return (
      <div className="adm-login">
        <section className="adm-section" aria-labelledby="sent-title" role="status">
          <MailCheck aria-hidden="true" size={32} strokeWidth={1.75} className="text-ocean" />
          <h1 id="sent-title" className="ds-text-title-1 m-0">
            {t("diver.login.sentTitle")}
          </h1>
          <p className="ds-text-body m-0">{t("diver.login.sentText", { email: sentTo })}</p>
          {import.meta.env.DEV ? (
            <p className="ds-text-footnote text-label-secondary m-0" data-testid="dev-hint">
              {t("diver.login.devHint")}
            </p>
          ) : null}
          <Button variant="tinted" onClick={() => setSentTo(null)}>
            {t("diver.login.otherEmail")}
          </Button>
        </section>
      </div>
    );
  }

  return (
    <div className="adm-login">
      <form
        className="adm-section"
        noValidate
        aria-labelledby="diver-login-title"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <h1
          id="diver-login-title"
          ref={heading}
          tabIndex={-1}
          className="ds-text-title-1 m-0 login-heading"
        >
          {registering ? t("diver.login.registerTitle") : t("diver.login.title")}
        </h1>
        {registrationClosed ? (
          <p className="adm-warning" role="status" data-testid="registration-closed">
            <Info aria-hidden="true" size={20} strokeWidth={1.75} />
            {t("diver.login.registrationClosed")}
          </p>
        ) : null}
        <p className="ds-text-subheadline text-label-secondary m-0">
          {registering ? t("diver.login.registerLead") : t("diver.login.lead")}
        </p>
        {linkErrorText ? (
          <p className="adm-error m-0" role="alert">
            {linkErrorText}
          </p>
        ) : null}
        <Field label={t("diver.login.email")}>
          {({ id }) => (
            <input
              id={id}
              name="username"
              className="adm-input"
              type="email"
              autoComplete="username webauthn"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>
        {registering ? (
          <Field label={t("diver.login.name")} hint={t("diver.login.nameHint")}>
            {({ id, describedBy }) => (
              <input
                id={id}
                className="adm-input"
                autoComplete="nickname"
                maxLength={120}
                aria-describedby={describedBy}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </Field>
        ) : null}
        {error ? (
          <p className="adm-error m-0" role="alert">
            {error}
          </p>
        ) : null}
        <Button type="submit" variant="filled" disabled={busy}>
          {registering ? t("diver.login.registerSend") : t("diver.login.send")}
        </Button>

        {registering ? (
          <p className="login-switch ds-text-subheadline m-0">
            <button
              type="button"
              className="ds-btn ds-btn--plain"
              onClick={() => switchTo("signin")}
            >
              {t("diver.login.toSignIn")}
            </button>
          </p>
        ) : (
          <>
            <button
              type="button"
              className="ds-btn ds-btn--plain login-disclosure"
              aria-expanded={passwordOpen}
              aria-controls={passwordId}
              onClick={() => {
                writeOpen(!passwordOpen);
                setPasswordOpen(!passwordOpen);
              }}
            >
              {t("diver.login.password")}
              <ChevronDown aria-hidden="true" strokeWidth={1.75} data-open={passwordOpen} />
            </button>
            <div id={passwordId} hidden={!passwordOpen} className="login-password">
              {passwordOpen ? (
                <>
                  <Field label={t("diver.login.passwordLabel")}>
                    {({ id }) => (
                      <input
                        id={id}
                        name="password"
                        className="adm-input"
                        type="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            void withPassword();
                          }
                        }}
                      />
                    )}
                  </Field>
                  <Button variant="filled" disabled={busy} onClick={() => void withPassword()}>
                    {t("diver.login.passwordSubmit")}
                  </Button>
                </>
              ) : null}
            </div>

            {passkeysSupported() ? (
              <Button
                variant="tinted"
                disabled={busy}
                icon={<KeyRound aria-hidden="true" strokeWidth={1.75} />}
                onClick={() => void passkey()}
              >
                {t("diver.login.passkey")}
              </Button>
            ) : null}

            {registrationClosed ? null : (
              <p className="login-switch ds-text-subheadline m-0">
                <button
                  type="button"
                  className="ds-btn ds-btn--plain"
                  onClick={() => switchTo("register")}
                >
                  {t("diver.login.toRegister")}
                </button>
              </p>
            )}
          </>
        )}
      </form>
    </div>
  );
}
